/* FLOW: reviews section (sections/product-reviews.liquid). The spread filters the list by rating,
   photos open in a viewer, and "Yorum yaz" opens Judge.me's own write-a-review form: Judge.me's
   widget is on the page out of sight (product-reviews.css), and this presses its button. If the form
   opens inside the widget instead of over the page, or the button never turns up, the widget is
   shown and scrolled to, so the form is always reachable. */

if (!customElements.get('product-reviews')) {
  const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Judge.me's write-a-review control: by its class, else by its words (Turkish or English)
  const WRITE_CLASS = /write[-_]?rev/i;
  const WRITE_TEXT = /(yorum|değerlendirme|inceleme)\s*(yaz|ekle|bırak|gönder)|write\s+(a\s+)?review/i;

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      this.reviews = [...this.querySelectorAll('.review')];
      this.rows = [...this.querySelectorAll('[data-filter]')];
      this.note = this.querySelector('[data-filter-note]');
      this.lightbox = this.querySelector('[data-review-lightbox]');

      this.rows.forEach((row) => row.addEventListener('click', () => this.filter(row.getAttribute('aria-pressed') === 'true' ? null : row.dataset.filter)));
      this.querySelector('[data-filter-clear]')?.addEventListener('click', () => this.filter(null));

      this.querySelectorAll('[data-review-open]').forEach((b) => b.addEventListener('click', () => this.writeReview(b)));
      this.querySelectorAll('[data-review-close]').forEach((b) => b.addEventListener('click', () => b.closest('dialog')?.close()));
      this.lightbox?.addEventListener('click', (e) => { if (e.target === this.lightbox) this.lightbox.close(); });
      this.querySelectorAll('[data-review-photo]').forEach((b) => b.addEventListener('click', () => {
        this.lightbox.querySelector('img').src = b.dataset.reviewPhoto;
        this.lightbox.showModal();
      }));

      // the spread's bars fill when the summary comes into view
      const bars = this.querySelector('.reviews__spread');
      if (bars && !still()) {
        this.classList.add('is-animated');
        this.observer = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          this.classList.add('is-in');
          this.observer.disconnect();
        }, { rootMargin: '0px 0px -15% 0px' });
        this.observer.observe(bars);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    filter(rating) {
      this.rows.forEach((row) => row.setAttribute('aria-pressed', String(row.dataset.filter === rating)));
      this.reviews.forEach((review) => {
        const show = !rating || review.dataset.rating === rating;
        const was = review.hidden;
        review.hidden = !show;
        if (show && was && !still()) {
          review.classList.remove('is-entering');
          void review.offsetWidth;
          review.classList.add('is-entering');
        }
      });
      if (this.note) {
        this.note.hidden = !rating;
        const shown = this.reviews.filter((r) => !r.hidden).length;
        this.note.querySelector('[data-filter-text]').textContent = rating ? `${rating} ★ · ${shown}` : '';
      }
    }

    /* ---- Judge.me's form ---------------------------------------------------- */

    writeReview(button) {
      const host = document.getElementById('judgeme_product_reviews');
      const section = host?.closest('.shopify-section');
      if (!host) return;

      // the widget's live controls only; its hidden server copy (.jdgm-legacy-widget-content) is inert
      const find = () => [...host.querySelectorAll('a, button, [role="button"]')].find((el) =>
        !el.closest('.jdgm-legacy-widget-content') && (WRITE_CLASS.test(el.className) || WRITE_TEXT.test(el.textContent || '')));

      const press = (target) => {
        button.classList.remove('is-loading');
        if (!target) return this.reveal(section);
        target.click();
        // a form over the page shows outside the widget; one inside it needs the widget shown
        setTimeout(() => {
          if (!this.judgemeFormOverPage(host)) this.reveal(section);
        }, 700);
      };

      const ready = find();
      if (ready) return press(ready);
      // the widget draws itself after the page loads: wait a little for its button
      button.classList.add('is-loading');
      const watch = new MutationObserver(() => {
        const target = find();
        if (!target) return;
        watch.disconnect();
        clearTimeout(timer);
        press(target);
      });
      watch.observe(host, { childList: true, subtree: true });
      const timer = setTimeout(() => {
        watch.disconnect();
        press(find());
      }, 6000);
    }

    /** is a Judge.me form or popup now showing outside the (hidden) widget? */
    judgemeFormOverPage(host) {
      return [...document.querySelectorAll('[class*="jdgm"]')].some((el) => {
        if (host.contains(el) || !/modal|popup|form|dialog/i.test(el.className)) return false;
        const box = el.getBoundingClientRect();
        return box.width > 0 && box.height > 0 && getComputedStyle(el).visibility !== 'hidden';
      });
    }

    reveal(section) {
      if (!section) return;
      section.classList.add('is-revealed');
      section.scrollIntoView({ behavior: still() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  customElements.define('product-reviews', ProductReviews);
}
